import React from 'react';
import { MapPin, Maximize2, Home, ArrowRight, Bed } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatPrice } from '../constants';
import { getOptimizedImageUrl } from '../lib/imageOptimizer';
import { trackEvent } from '../lib/analytics';

const PropertyCard = ({ property, onClick }) => {
    const handleCardClick = () => {
        trackEvent('select_item', {
            item_id: property.id,
            item_name: property.title
        });
        onClick();
    };

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ y: -12, scale: 1.01 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            onClick={handleCardClick}
            className={`bg-white border-0 shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer flex flex-col h-full group rounded-2xl overflow-hidden font-sans relative ${property.is_favorite ? 'ring-4 ring-[#EA723D]/30 shadow-[#EA723D]/20' : ''}`}
        >
            {/* Image + Badge Statut */}
            <div className="relative h-72 overflow-hidden">
                <div className="absolute inset-0 bg-black/10 z-10 group-hover:bg-black/0 transition-colors"></div>
                <img
                    src={getOptimizedImageUrl(property.image_url || property.image, 600) || 'https://via.placeholder.com/400x300'}
                    alt={property.title}
                    className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                    width="400"
                    height="300"
                    loading="lazy"
                />

                {/* Badges Container Top Left */}
                <div className="absolute top-4 left-4 z-20 flex flex-col gap-2 items-start">
                    {/* Sale/Rent Badge */}
                    <span className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg rounded-full backdrop-blur-md ${property.status === 'Location' ? 'bg-[#EA723D]/90' : 'bg-[#2C353D]/90'}`}>
                        {property.status === 'Location' ? 'Location' : 'Vente'}
                    </span>

                    {/* Marketing Status Badge */}
                    {property.marketing_status && property.marketing_status !== 'Disponible' && (
                        <span className={`px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg rounded-full backdrop-blur-md
                        ${['Nouveauté', 'Baisse de prix'].includes(property.marketing_status) ? 'bg-[#EA723D]/90' : ''}
                        ${property.marketing_status === 'Exclusivité' ? 'bg-[#2C353D]/90' : ''}
                        ${property.marketing_status === 'Sous Offre' ? 'bg-amber-500/90 animate-pulse' : ''}
                        ${property.marketing_status === 'Sous Compromis' ? 'bg-red-800/85' : ''}
                        ${property.marketing_status === 'Vendu' ? 'bg-red-600/90' : ''}
                        ${property.marketing_status === 'Loué' ? 'bg-green-600/90' : ''}
                    `}>
                            {property.marketing_status}
                        </span>
                    )}
                </div>

                {/* Top Right Container (Heart + Match) */}
                <div className="absolute top-4 right-4 z-10 flex flex-col items-end gap-2">
                    {/* Coup de Coeur Badge */}
                    {property.is_favorite && (
                        <span className="flex items-center gap-2 px-4 py-1.5 text-xs font-black uppercase tracking-wider text-white bg-gradient-to-r from-[#EA723D] to-[#F19163] shadow-xl rounded-full backdrop-blur-md border border-white/20 animate-pulse-slow">
                            <span className="text-base">❤️</span> Coup de Cœur
                        </span>
                    )}

                    {/* Match Score */}
                    {property.matchScore !== undefined && (
                        <span className={`px-3 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-lg rounded-full backdrop-blur-md ${property.matchScore === 100 ? 'bg-green-500/90' : 'bg-orange-500/90'}`}>
                            Match {property.matchScore}%
                        </span>
                    )}
                </div>

                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#2C353D]/90 via-[#2C353D]/60 to-transparent p-6 pt-12 flex items-end justify-between text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300 z-10">
                    <div className="flex items-center gap-2 text-sm font-medium tracking-wide">
                        <MapPin size={16} className="text-[#EA723D]" /> {property.city}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-8 flex flex-col flex-1 relative bg-white z-20">
                <div className="mb-4">
                    <h3 className="text-xl font-display font-bold text-[#2C353D] leading-tight group-hover:text-[#EA723D] transition-colors mb-2">{property.title}</h3>
                    {property.catch_phrase && (
                        <p className="text-sm text-gray-500 italic border-l-4 border-[#EA723D] pl-3 py-1 bg-gray-50/50 rounded-r">
                            "{property.catch_phrase}"
                        </p>
                    )}
                </div>

                {/* Missing Criteria Warning */}
                {property.missingCriteria && property.missingCriteria.length > 0 && (
                    <div className="mb-4 bg-orange-50 border border-orange-100 rounded-lg p-3">
                        <p className="text-[10px] font-bold text-orange-400 uppercase tracking-wide mb-1">Critères manquants :</p>
                        <div className="flex flex-wrap gap-1">
                            {property.missingCriteria.map((item, idx) => (
                                <span key={idx} className="text-[10px] font-bold text-orange-600 bg-orange-100 px-2 py-0.5 rounded">
                                    {item}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Features */}
                <div className="grid grid-cols-3 gap-2 mb-6 pb-6 border-b border-gray-100">
                    <div className="flex flex-col items-center justify-center p-2 bg-[#F7F7F7] rounded-lg">
                        <Home size={16} className="text-[#2C353D] mb-1" />
                        <span className="text-xs font-bold text-gray-600 truncate w-full text-center">{property.type}</span>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 bg-[#F7F7F7] rounded-lg">
                        <Maximize2 size={16} className="text-[#2C353D] mb-1" />
                        <span className="text-xs font-bold text-gray-600">{property.surface} m²</span>
                    </div>
                    <div className="flex flex-col items-center justify-center p-2 bg-[#F7F7F7] rounded-lg">
                        <Bed size={16} className="text-[#2C353D] mb-1" />
                        <span className="text-xs font-bold text-gray-600">{property.rooms} ch.</span>
                    </div>
                </div>

                {/* Price & CTA */}
                <div className="mt-auto flex items-center justify-between">
                    <div>
                        <p className="text-xs text-gray-400 uppercase font-bold mb-0.5">Prix</p>
                        <div className="text-2xl font-display font-black text-[#2C353D]">
                            {formatPrice(property.price)} {property.status === 'Location' && <span className="text-sm font-medium text-gray-500">/mois</span>}
                        </div>
                    </div>

                    <button className="w-12 h-12 rounded-full border-2 border-[#F7F7F7] text-[#2C353D] flex items-center justify-center group-hover:bg-[#2C353D] group-hover:text-white group-hover:border-[#2C353D] transition-all duration-300 shadow-sm group-hover:shadow-lg group-hover:scale-110">
                        <ArrowRight size={20} />
                    </button>
                </div>
            </div>
        </motion.div>
    );
};

export default PropertyCard;
